import React, { useMemo, useState } from "react";
import Section from "../components/Section";
import developerConfig from "../data/enkae.config.json";

// JSON renderer preserved for the easter-egg style viewer
function JSONLine({
  data,
  indent = 0,
  keyName = null,
  isLast = false,
  expandedFolders,
  setExpandedFolders,
  onNameClick
}: {
  data: any;
  indent?: number;
  keyName?: string | null;
  isLast?: boolean;
  expandedFolders: { [key: string]: boolean };
  setExpandedFolders: (fn: (prev: { [key: string]: boolean }) => { [key: string]: boolean }) => void;
  onNameClick: () => void;
}) {
  const indentStyle: React.CSSProperties = { paddingLeft: `${indent * 1.5}rem` };
  const isObject = typeof data === "object" && data !== null && !Array.isArray(data);
  const isArray = Array.isArray(data);

  if (isObject) {
    const entries = Object.entries(data);
    return (
      <>
        <div className="font-mono text-sm" style={indentStyle}>
          {keyName && <span className="text-pink-400">"{keyName}"</span>}
          {keyName && <span className="text-gray-400">: </span>}
          <span className="text-pink-300">{"{"}</span>
        </div>
        {entries.map(([k, v], idx) => {
          const isFolderKey = k.endsWith("/") && Array.isArray(v);
          const folderExpanded = expandedFolders[k];

          if (isFolderKey) {
            return (
              <div key={k}>
                <div className="font-mono text-sm" style={{ paddingLeft: `${(indent + 1) * 1.5}rem` }}>
                  <button
                    onClick={() => setExpandedFolders((prev) => ({ ...prev, [k]: !prev[k] }))}
                    className="text-left hover:bg-gray-700/30 rounded px-1 -ml-1 transition-colors cursor-pointer w-full"
                  >
                    <span className="text-pink-400">"{k}"</span>
                    <span className="text-gray-400">: </span>
                    <span className="text-cyan-400 hover:text-cyan-300">
                      {folderExpanded ? "▼" : "▶"}{" "}
                      <span className="text-gray-500 text-xs">({(v as any[]).length} items)</span>
                    </span>
                  </button>
                </div>
                {folderExpanded && (
                  <>
                    <div className="font-mono text-sm" style={{ paddingLeft: `${(indent + 2) * 1.5}rem` }}>
                      <span className="text-pink-300">[</span>
                    </div>
                    {(v as any[]).map((skill: string, skillIdx: number) => (
                      <div
                        key={skillIdx}
                        className="font-mono text-sm"
                        style={{ paddingLeft: `${(indent + 3) * 1.5}rem` }}
                      >
                        <span className="text-yellow-400">"{skill}"</span>
                        {skillIdx < (v as any[]).length - 1 && <span className="text-gray-400">,</span>}
                      </div>
                    ))}
                    <div className="font-mono text-sm" style={{ paddingLeft: `${(indent + 2) * 1.5}rem` }}>
                      <span className="text-pink-300">]</span>
                      {idx < entries.length - 1 && <span className="text-gray-400">,</span>}
                    </div>
                  </>
                )}
                {!folderExpanded && idx < entries.length - 1 && (
                  <div className="font-mono text-sm" style={{ paddingLeft: `${(indent + 1) * 1.5}rem` }}>
                    <span className="text-gray-400">,</span>
                  </div>
                )}
              </div>
            );
          }

          return (
            <JSONLine
              key={k}
              data={v}
              indent={indent + 1}
              keyName={k}
              isLast={idx === entries.length - 1}
              expandedFolders={expandedFolders}
              setExpandedFolders={setExpandedFolders}
              onNameClick={onNameClick}
            />
          );
        })}
        <div className="font-mono text-sm" style={indentStyle}>
          <span className="text-pink-300">{"}"}</span>
          {!isLast && <span className="text-gray-400">,</span>}
        </div>
      </>
    );
  }

  if (isArray) {
    return (
      <>
        <div className="font-mono text-sm" style={indentStyle}>
          {keyName && <span className="text-pink-400">"{keyName}"</span>}
          {keyName && <span className="text-gray-400">: </span>}
          <span className="text-pink-300">[</span>
        </div>
        {data.map((item: any, idx: number) => (
          <JSONLine
            key={idx}
            data={item}
            indent={indent + 1}
            isLast={idx === data.length - 1}
            expandedFolders={expandedFolders}
            setExpandedFolders={setExpandedFolders}
            onNameClick={onNameClick}
          />
        ))}
        <div className="font-mono text-sm" style={indentStyle}>
          <span className="text-pink-300">]</span>
          {!isLast && <span className="text-gray-400">,</span>}
        </div>
      </>
    );
  }

  const valueClass = typeof data === "string" ? "text-yellow-400" : "text-blue-400";
  const displayValue = typeof data === "string" ? `"${data}"` : String(data);
  const isNameKey = keyName === "name";

  return (
    <div className="font-mono text-sm" style={indentStyle}>
      {keyName && <span className="text-pink-400">"{keyName}"</span>}
      {keyName && <span className="text-gray-400">: </span>}
      {isNameKey ? (
        <span
          onClick={onNameClick}
          className={`${valueClass} cursor-pointer hover:text-yellow-300 transition-colors underline decoration-dotted`}
        >
          {displayValue}
        </span>
      ) : (
        <span className={valueClass}>{displayValue}</span>
      )}
      {!isLast && <span className="text-gray-400">,</span>}
    </div>
  );
}

export default function About() {
  const [expandedFolders, setExpandedFolders] = useState<{ [key: string]: boolean }>({});
  const [isSecretCommentVisible, setIsSecretCommentVisible] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const {
    name,
    role,
    location,
    preferredStack: preferredStackData,
    coreValues,
    specialties,
    skills,
    status,
    altStatus
  } = developerConfig as any;

  const skillGroups = useMemo(
    () => [
      { label: "Frontend", key: "frontend/", items: skills["frontend/"] ?? [] },
      { label: "Backend", key: "backend/", items: skills["backend/"] ?? [] },
      { label: "Database & Cloud", key: "database-cloud/", items: skills["database-cloud/"] ?? [] },
      { label: "Tools", key: "tools/", items: skills["tools/"] ?? [] },
      { label: "Design", key: "design/", items: skills["design/"] ?? [] }
    ],
    [skills]
  );

  const preferredStackGroups = [
    { label: "Frontend", key: "frontend/", items: preferredStackData["frontend/"] ?? [] },
    { label: "Backend", key: "backend/", items: preferredStackData["backend/"] ?? [] },
    { label: "Tools", key: "tools/", items: preferredStackData["tools/"] ?? [] }
  ];

  const specialtiesEntries = Object.entries(specialties);

  const handleSecretClick = () => {
    setIsSecretCommentVisible((prev) => !prev);
  };

  return (
    <main className="min-h-screen">
      <Section title="About" eyebrow="profile manifest" containerClassName="max-w-5xl">
        <div className="space-y-10">
          {isSecretCommentVisible && (
            <div className="text-xs font-mono text-text-subtle">// System notification: Developer profile accessed.</div>
          )}
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.65fr)]">
            <div className="bg-surface/80 border border-border-subtle rounded-card p-6 shadow-card">
              <p className="font-mono text-xs uppercase tracking-[0.4em] text-text-subtle mb-2">identity</p>
              <h1 className="text-3xl font-mono text-primary">{name}</h1>
              <p className="text-text-muted text-lg mt-2">{role}</p>
              <p className="text-sm text-text-subtle mt-4">{status}</p>
              {altStatus && (
                <p className="text-xs text-text-subtle/80 mt-1">{altStatus}</p>
              )}
            </div>
            <div className="bg-surface/60 border border-border-subtle rounded-card p-6 shadow-card space-y-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle">Location</p>
                <p className="text-lg text-primary">{location}</p>
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle">Preferred Stack</p>
                <div className="mt-2 space-y-2">
                  {preferredStackGroups.map(({ label, items }) => (
                    <div key={label} className="text-sm text-text-muted flex flex-wrap gap-2">
                      <span className="font-mono text-xs uppercase text-text-subtle">{label}</span>
                      <span className="opacity-50">•</span>
                      <span>{items.join(", ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-surface/80 border border-border-subtle rounded-card p-6 shadow-card">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle mb-3">Core Values</p>
            <div className="flex flex-wrap gap-3">
              {coreValues.map((value) => (
                <span
                  key={value}
                  className="px-3 py-1 rounded-pill border border-border-subtle text-sm text-primary/90 bg-surface/70"
                >
                  {value}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-surface/70 border border-border-subtle rounded-card p-6 shadow-card">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle mb-5">Specialties</p>
            <div className="grid gap-4 sm:grid-cols-3">
              {specialtiesEntries.map(([key, description]) => (
                <div key={key} className="border border-border-subtle/70 rounded-lg p-4 bg-surface/60">
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-accent mb-2">{key}</p>
                  <p className="text-sm text-text-muted">{description}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle mb-4">Skills</p>
            <div className="grid gap-4 md:grid-cols-2">
              {skillGroups.map(({ label, key, items }) => (
                <div
                  key={key}
                  className="border border-border-subtle rounded-card p-4 bg-surface/70 shadow-card/30 backdrop-blur-sm"
                >
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-accent mb-2">{label}</p>
                  <div className="flex flex-wrap gap-2 text-sm text-text-muted">
                    {items.map((skill) => (
                      <span key={skill} className="px-2 py-1 rounded-pill border border-border-subtle/60">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-surface/80 border border-border-subtle rounded-card p-6 shadow-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-text-subtle">
                  Developer profile JSON
                </p>
                <p className="text-sm text-text-muted mt-1">Bonus reference for anyone inspecting the source.</p>
              </div>
              <button
                onClick={() => setShowConfig((prev) => !prev)}
                className="font-mono text-xs uppercase tracking-[0.3em] px-3 py-1 rounded-pill border border-border-subtle/70 text-text-muted hover:text-primary hover:border-accent transition"
              >
                {showConfig ? "Hide" : "View"}
              </button>
            </div>
            {showConfig && (
              <div className="mt-4 rounded-lg border border-border-subtle bg-black/40 overflow-x-auto max-h-[360px] p-4">
                <div className="font-mono text-sm">
                  <span className="text-pink-300">{"{"}</span>
                  <JSONLine
                    data={developerConfig}
                    indent={1}
                    expandedFolders={expandedFolders}
                    setExpandedFolders={setExpandedFolders}
                    onNameClick={handleSecretClick}
                  />
                  <span className="text-pink-300">{"}"}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </Section>
    </main>
  );
}
